import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bell, Check, ExternalLink } from 'lucide-react';
import {
  BILLING_INTERVAL_LABELS,
  CR_CHANNEL_LABELS,
  CR_CLASSIFICATION_LABELS,
  CR_PRIORITY_LABELS,
  CR_RECOMMENDATION_LABELS,
  CR_STATUS_LABELS,
  INVOICE_BASIS_LABELS,
  INVOICE_STATUS_LABELS,
  INVOICED_STATUS,
  UNCOMPLETED_MONTH,
  canOpenInvoice,
  crLabel,
  formatDate,
  formatHours,
  formatMonthYear,
  formatPercent,
  formatReportValue,
  serviceHoursTimeline,
  type BillingInterval,
  type EffectiveHourlyRate,
  type Paginated,
  type PartnerCommissionResponse,
  type PartnerReferral,
  type ReportDocument,
  type ServiceAgreementTerms,
} from '@codepass/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { invoicePath } from '@/lib/paths';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Input,
  Modal,
  Progress,
  Select,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@/components/ui';
import {
  BarChart,
  ContractStatusBadge,
  ContractTypeBadge,
  EmptyState,
  HoursValue,
  InvoiceStatusBadge,
  MetricCard,
  Money,
  PageHeader,
  Pagination,
  Percent,
  PermittedLink,
  SeverityBadge,
  TimeEntryStatusBadge,
} from '@/components/domain';
import { cn } from '@/lib/cn';
import { SubtaskModal } from '@/components/SubtaskModal';
import { BurndownChart } from '@/components/BurndownChart';
import { ContractBufferCard } from '@/components/ContractBufferCard';
import {
  AgreementStatusBadge,
  CommissionLinesTable,
  rateLabel,
} from '@/components/PartnerCommission';
import type {
  AlertItem,
  ChangeRequestItem,
  ChangeRequestsResponse,
  ContractDetail,
  ContractHourItem,
  ContractHoursByMonthResponse,
  ContractHoursResponse,
  ContractLineItem,
  ContractMilestoneItem,
  ContractMilestonesResponse,
  HoursLinkStatus,
  InvoiceListItem,
  ServiceHourItem,
} from '@/types';

type Tab =
  'oversigt' | 'milepaele' | 'timer' | 'fakturaer' | 'linjer' | 'tillaeg' | 'burndown' | 'partner';

/**
 * Fanerne. Tillægsaftaler er reciprok: en hovedaftale viser sine tillægsaftaler, en tillægsaftale
 * viser i stedet hovedaftalen den hænger på — samme fane-id, modsat retning i kæden.
 *
 * **En serviceaftale har tre.** Milepæle, kontraktlinjer og tillægsaftaler bruges ikke på den:
 * den har én tavle i OnePro og ingen faser, ingen produktlinjer i CRM, og ændringer aftales som
 * nye opgaver frem for som tillæg. Tomme faner ville kun sige det samme tre gange.
 *
 * **Partneraftale only when the sale is referred** - on both kinds of agreement: a service
 * agreement earns commission on its running services like a project does on its hours. Not
 * referred, no tab - it would be empty on almost every contract.
 *
 * **Burndown only with a board and the right to the report** - the curve is the burndown report's
 * for the contract's board in OnePro, so a role that may not see /rapporter/burndown does not see
 * it here either, and a contract with no board has nothing to draw. Not on a service agreement:
 * it runs until it is cancelled, so there is no end to burn down to.
 */
function contractTabs(
  isAmendment: boolean,
  isService: boolean,
  isReferred: boolean,
  hasBurndown: boolean,
): Array<{ id: Tab; label: string; countKey?: string }> {
  const partner = isReferred ? [{ id: 'partner' as const, label: 'Partneraftale' }] : [];

  if (isService) {
    return [
      { id: 'oversigt', label: 'Oversigt' },
      { id: 'timer', label: 'Timer', countKey: 'hours' },
      { id: 'fakturaer', label: 'Fakturaer', countKey: 'invoices' },
      ...partner,
    ];
  }

  return [
    { id: 'oversigt', label: 'Oversigt' },
    { id: 'milepaele', label: 'Milepæle', countKey: 'milestones' },
    /*
     * Ingen Opgaver-fane. Opgaven er ikke en selvstaendig ting for en controller - den er den
     * overskrift, timerne grupperes under, baade i faktureringsmodellen og i Timer-fanens
     * Opgave-kolonne. En fane der kun viste opgavenavne ville vaere det samme opslag ét klik
     * laengere vaek. `GET /contracts/:id/tasks` findes stadig; det er kun fanen der er vaek.
     */
    { id: 'timer', label: 'Timer', countKey: 'hours' },
    { id: 'fakturaer', label: 'Fakturaer', countKey: 'invoices' },
    { id: 'linjer', label: 'Kontraktlinjer' },
    isAmendment
      ? { id: 'tillaeg', label: 'Hovedaftale' }
      : { id: 'tillaeg', label: 'Tillægsaftaler', countKey: 'change_requests' },
    ...(hasBurndown ? [{ id: 'burndown' as const, label: 'Burndown' }] : []),
    ...partner,
  ];
}

export function ContractDetailPage() {
  const { id = '' } = useParams();
  const { canSeeReport } = useAuth();
  const [tab, setTab] = useState<Tab>('oversigt');

  // Skift af kontrakt (fx via Hovedaftale- eller Tillægsaftaler-linket) genbruger den samme
  // komponent, saa den valgte fane ville ellers hænge ved. Start altid forfra paa Oversigt.
  useEffect(() => {
    setTab('oversigt');
  }, [id]);

  const {
    data: contract,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => api.get<ContractDetail>(`/contracts/${id}`),
    enabled: Boolean(id),
  });

  if (isLoading) return <Skeleton className="h-64" />;
  if (error || !contract) {
    return <Alert tone="error">Kunne ikke hente kontrakten.</Alert>;
  }

  const isAmendment = Boolean(contract.parent_contract_id);
  const service = contract.service_agreement ?? null;
  /* The board is the one the Deadline row in Detaljer is read from - see contract-deadline.ts. */
  const burndownContractNo =
    canSeeReport('burndown') && contract.onepro_deadline?.link === 'linked'
      ? contract.contract_no
      : null;
  const tabs = contractTabs(
    isAmendment,
    service !== null,
    Boolean(contract.partner),
    Boolean(burndownContractNo),
  );

  return (
    <>
      <PageHeader
        title={contract.title}
        description={contract.customer?.name ?? undefined}
        /*
          Ingen 'Opret fakturaudkast' herfra. Fakturering starter paa /fakturering/ny med
          kunden og perioden som grundlag - én faktura kan daekke flere af kundens aftaler - og
          en genvej fra én kontrakt ville invitere til at fakturere aftale for aftale.
        */
        actions={
          <>
            {/* Alarmer slås op på aftalens nummer — se `contract_no` i codepass_alarms. */}
            <ContractAlertsButton
              contractKey={contract.contract_no ?? contract.id}
              count={contract.counts?.alerts ?? 0}
            />
            {contract.getaccept_url ? (
              <a href={contract.getaccept_url} target="_blank" rel="noreferrer">
                <Button variant="secondary" size="sm">
                  Åbn i GetAccept
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </a>
            ) : null}
          </>
        }
      />

      {contract.parent ? (
        <Alert tone="info" className="mb-4">
          Dette er en tillægsaftale til{' '}
          <Link to={`/kontrakter/${contract.parent.id}`} className="font-medium underline">
            {contract.parent.title}
          </Link>
          .
        </Alert>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <ContractStatusBadge status={contract.status} />
        {/*
          CRM's egen etiket på en serviceaftale. Codepass' udledning — `recurring` — hedder
          'Abonnement', og det er en anden ting end det, der står på aftalen i Dynamics.
        */}
        {service ? (
          <Badge tone="neutral">{contract.contract_type_label ?? 'Serviceaftale'}</Badge>
        ) : (
          <ContractTypeBadge type={contract.contract_type} />
        )}
        {contract.billing_interval ? (
          <Badge tone="neutral">
            {BILLING_INTERVAL_LABELS[contract.billing_interval as BillingInterval]}
          </Badge>
        ) : null}
      </div>

      {/*
        Fem kort. 'Dage til slut' er taget af — nedtællingen er alarmernes arbejde, ikke et felt
        man aflæser, og reglen `contract_expiring` siger til i god tid. Fem på en række på en
        bred skærm, tre på en almindelig; beløbene skal kunne læses.

        En serviceaftale har sine egne fire — se `ServiceAgreementCards`.
      */}
      {service ? (
        <ServiceAgreementCards contract={contract} terms={service} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
          {/*
          'CR Sum' under kontraktsummen: hvad de godkendte ændringer lægger oven i den aftalte
          sum. Står som sin egen linje og ikke lagt ind i beløbet ovenover — kontraktsummen er
          det, der er underskrevet, og de to tal skal kunne skelnes.
        */}
          <MetricCard
            label="Kontraktsum"
            value={<Money value={contract.contract_sum_excl_vat} decimals={0} />}
            hint={<ChangeRequestSumHint contract={contract} />}
          />
          {/*
          Procenten måler mod kontraktsummen **plus** de fakturerbare ændringer — serveren
          regner den, se `billingBase` i live-contracts.ts. Uden det ville en aftale stå på
          100 %, mens der stadig var godkendt merarbejde tilbage at fakturere.
        */}
          <MetricCard
            label="Faktureret"
            value={<Money value={contract.billed_excl_vat} decimals={0} />}
            hint={<Percent value={contract.billed_pct} />}
          />
          {/*
          PRD par. 5.2: fire metrics - kontraktsum, faktureret, timer (brugt/loft), dage til slut.

          **Loftet** indeholder estimatet fra alle ændringsanmodninger, ikke kun de fakturerbare:
          budgettet måler omfanget af arbejdet, ikke hvem der betaler for det. En 'Præcisering i
          scope' er arbejde, kunden allerede har betalt for — den udvider opgaven, ikke regningen,
          og derfor rører den ikke 'Kontraktsum' eller 'Faktureret'.

          **Forbruget** er OnePro's faktiske timer alene. En anmodning, der er skubbet til OnePro,
          er en opgave på projektets tavle, så den tid, der rent faktisk bruges på den, tælles med
          derfra. Estimatet hører til på højresiden, den faktiske tid på venstre. Tiden i
          "General" tæller ikke — arbejde uden for aftalen bruger ikke af loftet, som på Burndown
          (15-10-2026). Hintet siger, hvor meget der er lagt udenfor.

          Hintet siger hvor stor en del af loftet, der er kommet til via ændringer. Serveren
          regner det — se `applyChangeRequestsToContract` i @codepass/shared.
        */}
          <MetricCard
            label={contract.hours_cap ? 'Timer af loft' : 'Timer brugt'}
            value={
              contract.hours_cap
                ? `${formatHours(contract.used_hours)} / ${formatHours(contract.hours_cap)}`
                : formatHours(contract.used_hours)
            }
            hint={<HoursCapHint contract={contract} />}
            tone={
              contract.hours_cap && contract.used_hours / contract.hours_cap >= 0.8
                ? 'warning'
                : 'neutral'
            }
          />
          {/*
          Aftalens samlede buffer og hvor langt fakturaerne er nået. Intern: beløbet ligger ude
          i linjernes antal, så kunden ser det ikke. Målet sættes på kortet.
        */}
          <ContractBufferCard contract={contract} queryKey={['contract', id]} />
          {/*
          Hvad aftalen faktisk indbringer pr. brugt time. Ikke-fakturerbart arbejde på tavlen —
          'Ikke fakturerbar', 'Intern', 'Salg' — står i nævneren, men ikke i tælleren, og trækker
          derfor prisen ned. Serveren regner tallet (`effectiveHourlyRate`), så kortet og en
          kommende regel ikke kan nå hver sit svar; hintet siger hvorfor prisen står, hvor den står.

          Ændringsanmodningernes estimat tæller **ikke** med: en anmodning, der er skubbet til
          OnePro, er en opgave på tavlen, så dens faktiske tid er der allerede. Nævneren her er
          forbruget på 'Timer af loft' plus tiden i "General", som faktureres som alt andet.
          Anmodningerne sætter loftet på det kort — estimatet hører til på budgetsiden, ikke i en
          pris pr. brugt time.
        */}
          <MetricCard
            label="Reel timepris"
            value={
              contract.effective_rate?.real_rate_excl_vat === null ||
              contract.effective_rate === undefined ? (
                '—'
              ) : (
                <Money value={contract.effective_rate.real_rate_excl_vat} decimals={0} />
              )
            }
            hint={<RealRateHint rate={contract.effective_rate} />}
            tone={contract.effective_rate?.level === 'warning' ? 'warning' : 'neutral'}
          />
        </div>
      )}

      <nav className="mt-6 flex flex-wrap gap-1 border-b border-line" role="tablist">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm',
              tab === item.id
                ? 'border-brand font-medium text-brand-800'
                : 'border-transparent text-ink-muted hover:text-ink',
            )}
          >
            {item.label}
            {item.countKey && contract.counts[item.countKey] ? (
              <span className="ml-1 text-xs text-ink-muted">
                ({contract.counts[item.countKey]})
              </span>
            ) : null}
          </button>
        ))}
      </nav>

      <div className="mt-4">
        {tab === 'oversigt' ? <OverviewTab contract={contract} /> : null}
        {tab === 'milepaele' ? <MilestonesTab contractId={id} /> : null}
        {tab === 'timer' ? (
          service ? (
            <ServiceHoursTab contractId={id} />
          ) : (
            <HoursTab contractId={id} />
          )
        ) : null}
        {tab === 'fakturaer' ? <InvoicesTab contractId={id} /> : null}
        {tab === 'linjer' ? <LinesTab contractId={id} /> : null}
        {/*
          Fanen er ændringsanmodningerne. Tillægsaftalerne er taget af: en opportunity i Dynamics
          bærer ingen `parent_contract_id`, så listen var tom på hver eneste aftale, og en tom
          tabel med en forklaring under er ikke oplysning — det er støj over det, fanen faktisk
          har at vise. `GET /contracts/:id/amendments` findes stadig; det er kun visningen der er
          væk.
        */}
        {tab === 'tillaeg' ? (
          isAmendment && contract.parent ? (
            <ParentTab parent={contract.parent} />
          ) : (
            <ChangeRequestsCard contractId={id} />
          )
        ) : null}
        {tab === 'burndown' && burndownContractNo ? (
          <BurndownTab contractNo={burndownContractNo} />
        ) : null}
        {tab === 'partner' && contract.partner ? (
          <PartnerTab contractId={id} referral={contract.partner} currency={contract.currency} />
        ) : null}
      </div>
    </>
  );
}

/**
 * Oversigt-tab (PRD par. 5.2): venstre kolonne (60 %) beskrivelse og relaterede alarmer;
 * hoejre kolonne (40 %) ansvarlig PM, kundekontakt og datoer.
 *
 * **Afviger fra PRD par. 5.2.** Specen laegger ogsaa kundedata, faktureringsinterval,
 * betalingsbetingelser og ufaktureret WIP i venstre kolonne. De er taget af: kunde, CVR og
 * kontakt staar i forvejen i Detaljer til hoejre, faktureringsintervallet staar som badge
 * over fanerne, og betalingsbetingelser og momsbehandling hoerer paa /kunder, hvor de
 * kommer fra. Kortet er derfor beskrivelsen og ingenting andet.
 */
function OverviewTab({ contract }: { contract: ContractDetail }) {
  const { can } = useAuth();
  return (
    <div className="grid gap-4 lg:grid-cols-5">
      <div className="space-y-4 lg:col-span-3">
        <Card>
          <CardHeader>
            <CardTitle>Beskrivelse</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4">
            <p className="text-sm">{contract.description ?? 'Ingen beskrivelse fra GetAccept.'}</p>
            {contract.service_agreement ? (
              <ServiceTermsBox terms={contract.service_agreement} />
            ) : contract.contract_type === 'recurring' ? (
              <div className="rounded-md bg-canvas p-3 text-sm">
                <p>
                  Abonnement: <Money value={contract.recurring_amount_excl_vat} /> pr. periode
                </p>
                <p className="mt-1 text-xs text-ink-muted">
                  Næste fakturadato: {formatDate(contract.recurring_next_invoice_date)}
                </p>
              </div>
            ) : null}
          </CardBody>
        </Card>

        {can('alerts.view_own') ? (
          <RelatedAlertsCard contractKey={contract.contract_no ?? contract.id} />
        ) : null}
      </div>

      <Card className="lg:col-span-2">
        <CardHeader className="flex items-center justify-between gap-3">
          <CardTitle>Detaljer</CardTitle>
          {/*
            Kontraktnummeret som dæmpet tekst i stedet for en række i listen nedenfor: det er
            aftalens navn i Dynamics og den nøgle, der står i adresselinjen, så det hører til
            som overskrift på kortet frem for som ét felt blandt ti. Vises kun når der er et —
            5 af 25 aftaler i Dynamics mangler nummeret.
          */}
          {contract.contract_no ? (
            <span className="tabular text-xs text-ink-muted">{contract.contract_no}</span>
          ) : null}
        </CardHeader>
        <CardBody>
          <dl className="space-y-2 text-sm">
            <Row label="Ansvarlig PM" value={contract.pm?.name ?? 'Ikke tildelt'} />
            <Row label="Kunde" value={contract.customer_detail?.name ?? '—'} />
            <Row label="CVR" value={contract.customer_detail?.cvr ?? '—'} />
            {/*
              Kontaktpersonen fra CRM — feltet `Contact` på salgsmuligheden, udfyldt på alle 25
              aftaler. Ikke kontakten fra kundekortet i Business Central: den hører til kunden
              og står på /kunder/:id, mens denne er den person, *aftalen* er lavet med.
            */}
            <Row label="Kontakt" value={contactValue(contract.crm_contact)} />
            <Row label="Signeret" value={signedValue(contract)} />
            {/*
              Kun når der er en underskriver at vise. CRM har ikke noget "signeret af" på
              salgsmuligheden, så rækken ville stå tom på hver eneste Dynamics-aftale — samme
              valg som GetAccept-ID nedenfor.
            */}
            {contract.signed_by_customer ? (
              <Row label="Signeret af" value={contract.signed_by_customer} />
            ) : null}
            {/*
              Ingen Start- og Slut-rækker. Aftalens periode står ingen steder på siden nu — heller
              ikke som hint under 'Dage til slut', som er taget af. Det er med vilje: udløbet er
              alarmernes arbejde (reglen `contract_expiring`), og perioden hentes i Dynamics, når
              nogen skal bruge den. Skal datoerne tilbage, hører de til her.
            */}
            {/*
              The deadline is OnePro's, not the CRM period above: the date set on the contract's
              board. Shown on every contract, also without one - an empty row says where to set it.
            */}
            <Row label="Deadline" value={deadlineValue(contract.onepro_deadline)} />
            {/*
              Kun når kontrakten faktisk kommer fra GetAccept. En kontrakt læst fra Dynamics
              har intet GetAccept-ID, og rækken ville stå tom med en etiket der lover et felt,
              der ikke findes for den kilde. Samme valg som på /kunder/:id, hvor felter uden
              en BC-kilde er taget af kortet frem for at stå tomme.
            */}
            {contract.getaccept_id ? (
              <Row label="GetAccept-ID" value={contract.getaccept_id} />
            ) : null}
            <Row
              label="Sidst synkroniseret"
              value={contract.last_synced_at ? formatDate(contract.last_synced_at) : 'Aldrig'}
            />
          </dl>
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * Kontaktpersonen som rækken viser den: navn, titel og mail, hvis kortet i CRM har dem.
 *
 * Kun navnet er sikkert udfyldt. Titlen står i parentes frem for som egen række — den er en
 * oplysning om personen, ikke om aftalen — og mailen er den del, man handler på.
 */
function contactValue(contact: ContractDetail['crm_contact']): string {
  if (!contact) return '—';
  const title = contact.job_title ? ` (${contact.job_title})` : '';
  const email = contact.email ? ` · ${contact.email}` : '';
  return `${contact.name}${title}${email}`;
}

/**
 * Rækken 'Signeret': ja eller nej — og datoen, når der er en.
 *
 * Rækken viste før kun datoen, `actualclosedate` fra CRM, som så skulle læses som et ja. Den
 * blev et ja/nej, fordi spørgsmålet på et kontraktkort først og fremmest er, *om* aftalen er
 * underskrevet. Datoen står nu ved siden af svaret frem for i stedet for det: `Ja · 20-04-2026`
 * svarer på begge, og `Ja` alene siger, at nogen har sat afkrydsningen uden at der findes en
 * dato.
 *
 * To signaler kan sige ja, og begge tæller: lukkedatoen er sat (aftalen er vundet), eller
 * afkrydsningen `Kontrakt signeret` er sat i CRM. Ellers nej — **også når ingen af dem er
 * udfyldt.** Em-streg ville lade en usigneret aftale se ud, som om svaret var ukendt, og det
 * er det ikke: der er ingen underskrift.
 */
function signedValue(contract: ContractDetail): string {
  const signed = Boolean(contract.signed_date) || contract.contract_signed === true;
  if (!signed) return 'Nej';

  return contract.signed_date ? `Ja · ${formatDate(contract.signed_date)}` : 'Ja';
}

/**
 * The row 'Deadline': the date on the contract's board in OnePro, or why there is none. "Ikke sat"
 * and "no board" are told apart, because they are fixed in different places - the board in OnePro,
 * or the project's link in Orchestrator. An em dash when OnePro could not be read.
 */
function deadlineValue(deadline: ContractDetail['onepro_deadline']): string {
  if (!deadline) return '—';
  if (deadline.link !== 'linked') return 'Ingen tavle i OnePro';
  return deadline.date ? formatDate(deadline.date) : 'Ikke sat i OnePro';
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}

/**
 * Alarmknappen i sidehovedet, med kontraktens egne alarmer i et vindue.
 *
 * Modal frem for en fane: alarmerne er noget man tjekker, mens man kigger på aftalen — ikke et
 * sted man arbejder. Knappen viser antallet, så man kan se der er noget uden at klikke, og den
 * står helt væk, når der ikke er nogen. En knap, der altid er tom, holder man op med at trykke på.
 *
 * Listen hentes med det samme filter, kortet 'Relaterede alarmer' bruger — samme svar, to
 * steder — og først når vinduet åbnes.
 */
function ContractAlertsButton({ contractKey, count }: { contractKey: string; count: number }) {
  const [open, setOpen] = useState(false);
  /* Uden 'Se mine alarmer' er der ingen alarmer at vise - heller ikke et antal. */
  const seesAlerts = useAuth().can('alerts.view_own');

  const { data, isLoading } = useQuery({
    queryKey: ['contract', contractKey, 'alerts'],
    queryFn: () => api.get<Paginated<AlertItem>>('/alerts', { contract_id: contractKey }),
    enabled: open && seesAlerts,
  });

  if (count === 0 || !seesAlerts) return null;
  const alerts = data?.data ?? [];

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        <Bell className="h-3.5 w-3.5" />
        Alarmer ({count})
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Alarmer på aftalen"
        description="Åbne alarmer rejst af regelkørslen. Klik for at behandle dem."
      >
        {isLoading ? (
          <Skeleton className="h-24" />
        ) : alerts.length === 0 ? (
          <p className="text-sm text-ink-muted">Ingen aktive alarmer på kontrakten.</p>
        ) : (
          <ul className="space-y-3">
            {alerts.map((alert) => (
              <li key={alert.id} className="border-b border-line pb-3 last:border-0 last:pb-0">
                <Link to={`/alarmer/${alert.id}`} className="flex items-start gap-2 text-sm">
                  <SeverityBadge severity={alert.severity} />
                  <span className="min-w-0">
                    <span className="font-medium hover:underline">{alert.title}</span>
                    <span className="mt-0.5 block text-xs text-ink-muted">{alert.description}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </>
  );
}

/**
 * Relaterede alarmer i Oversigt-tabben. Henter kontraktens aktive alarmer og linker til dem.
 * PRD par. 5.2.
 */
function RelatedAlertsCard({ contractKey }: { contractKey: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['contract', contractKey, 'alerts'],
    queryFn: () => api.get<Paginated<AlertItem>>('/alerts', { contract_id: contractKey }),
  });

  const alerts = data?.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Relaterede alarmer</CardTitle>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <Skeleton className="h-16" />
        ) : alerts.length === 0 ? (
          <p className="text-sm text-ink-muted">Ingen aktive alarmer på kontrakten.</p>
        ) : (
          <ul className="space-y-2">
            {alerts.map((alert) => (
              <li key={alert.id}>
                <Link
                  to={`/alarmer/${alert.id}`}
                  className="flex items-start gap-2 text-sm hover:underline"
                >
                  <SeverityBadge severity={alert.severity} />
                  <span className="min-w-0">{alert.title}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Tavlerne faserne står på, som én læsbar sætning.
 *
 * En aftale kan dække to projekter i Orchestrator og dermed to tavler — samme forbehold som
 * timerne tager. Står faserne på flere, nævnes de alle, så en fase man ikke genkender kan
 * findes i OnePro.
 */
function boardCaption(milestones: ContractMilestoneItem[]): string | null {
  const names = [
    ...new Set(
      milestones
        .map((milestone) => milestone.board_name?.trim())
        .filter((name): name is string => Boolean(name)),
    ),
  ];
  if (names.length === 0) return null;

  const boards =
    names.length === 1
      ? `tavlen ${names[0]}`
      : `tavlerne ${names.slice(0, -1).join(', ')} og ${names[names.length - 1]}`;
  const caption =
    `Faserne på ${boards} i OnePro, i den rækkefølge de står der. ` +
    'En milepæl er færdig, når den er lukket i OnePro. Lukkes den med estimat tilbage, er resten ' +
    'ubrugt og trukket fra loftet på Burndown den dag, den lukkede — den sidste dag, der ' +
    'skete noget i den, for OnePro gemmer ikke, hvornår en milepæl lukkes.';
  return milestones.some((milestone) => milestone.out_of_scope)
    ? `${caption} ${OUT_OF_SCOPE_HINT}`
    : caption;
}

/** Hvorfor "General" står uden estimat — se `isOutOfScopeCategory` i @codepass/shared. */
const OUT_OF_SCOPE_HINT =
  'General er arbejde uden for aftalen, så estimaterne der tæller ikke med; timerne gør.';

/**
 * Forbruget mod estimatet, i procent. Null når fasen ikke har et estimat at måle mod.
 *
 * Uden estimat er der ingen brøk: 46,50 timer ud af ingenting er ikke 0 % og heller ikke
 * 100 %. Bjælken udelades da helt frem for at vise en tom rende, der ligner "ingen timer brugt".
 */
function estimateUsage(milestone: ContractMilestoneItem): number | null {
  if (milestone.estimated_hours <= 0) return null;
  return (milestone.used_hours / milestone.estimated_hours) * 100;
}

/**
 * Kvitteringen på en milepæl: alle dens opgaver er meldt til opkrævning.
 *
 * Samme port som /fakturering/ny vælger timer efter — `invoice_pending` med et
 * `invoice_pending_id`, eller allerede sendt. Se `isReadyToBill` i API'et.
 *
 * Tre tilstande og ikke to: en fase uden opgaver er ikke "ikke klar", den er tom, og en
 * advarselsfarve på den ville bede nogen om at gøre noget, der ikke findes.
 */
function MilestoneReadiness({ milestone }: { milestone: ContractMilestoneItem }) {
  if (milestone.task_count === 0) {
    return <span className="text-xs text-ink-muted">Ingen opgaver</span>;
  }

  if (milestone.ready_for_invoicing) {
    /*
     * Kun fluebenet. Teksten stod der før, men kolonnens to andre tilstande — 'Ingen opgaver' og
     * '3 af 5 klar' — siger allerede hvad kolonnen handler om, så 'Klar til fakturering' gentog
     * overskriften på hver eneste færdige række.
     *
     * `title` og `aria-label` bærer den: et grønt flueben uden tekst er ingenting for en
     * skærmlæser, og et museophold skal stadig kunne sige hvad det betyder.
     */
    return (
      <Badge tone="success" title="Klar til fakturering">
        <Check className="inline h-3 w-3" aria-label="Klar til fakturering" role="img" />
      </Badge>
    );
  }

  return (
    <span className="text-xs text-ink-muted tabular">
      {milestone.ready_count} af {milestone.task_count} klar
    </span>
  );
}

/**
 * Om milepælen er færdig: lukket i OnePro (`todo_categories.is_open`). Se `isMilestoneClosed` i
 * @codepass/shared — den afløste "alle opgaver er færdige" 14-10-2026.
 *
 * Med dagen den lukkede, som Burndown regner den (15-10-2026) — så faldet i kurven kan findes.
 */
function MilestoneState({ milestone }: { milestone: ContractMilestoneItem }) {
  if (!milestone.closed) return <span className="text-xs text-ink-muted">Åben</span>;
  return (
    <>
      <Badge tone="success">Færdig</Badge>
      {milestone.closed_on ? (
        <div className="mt-1 text-xs text-ink-muted tabular">
          Lukket {formatDate(milestone.closed_on)}
        </div>
      ) : null}
    </>
  );
}

/**
 * F1.6 — fanen 'Milepæle'.
 *
 * **En milepæl er en fase på aftalens OnePro-tavle** — en række i `public.todo_categories`.
 * Se contract-milestones.ts i API'et for hvorfor kilden er tavlen og ikke `company_id` alene.
 *
 * Fasen selv bærer hverken beløb eller dato — kun om den er lukket, og det er milepælens status.
 * Tallene er forbrugt tid på **opgaverne** i den mod fasens eget estimat (`time_estimate`, ikke
 * opgavernes — 16-10-2026, som på Burndown), og hvor mange af opgaverne der er meldt klar til
 * opkrævning. "General" står uden estimat: det er arbejde uden for aftalen.
 *
 * Handlingerne 'Markér som nået' og 'Opret fakturaudkast' hører til `milestones`-tabellen i
 * Codepass' eget skema og står derfor ikke her: en fase har ingen status at rykke og intet
 * beløb at fakturere. `POST /milestones/:id/...` findes stadig i API'et.
 */
function MilestonesTab({ contractId }: { contractId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['contract', contractId, 'milestones'],
    queryFn: () => api.get<ContractMilestonesResponse>(`/contracts/${contractId}/milestones`),
  });

  if (isLoading) return <Skeleton className="h-32" />;
  if (!data || data.data.length === 0) {
    const empty = linkEmptyState(data?.link_status, data?.link_projects, MILESTONE_SUBJECT);
    return (
      <Card>
        <EmptyState title={empty.title} description={empty.description} />
      </Card>
    );
  }

  const caption = boardCaption(data.data);

  /*
   * Tavlens navn står kun på rækken, når aftalen har mere end én. Med kun én siger
   * billedteksten det allerede, og den ville så stå gentaget på hver eneste række.
   */
  const boards = new Set(
    data.data.map((milestone) => milestone.board_name?.trim()).filter(Boolean),
  );
  const showBoard = boards.size > 1;

  return (
    <Card>
      <Table>
        <thead>
          <tr>
            <Th>Milepæl</Th>
            <Th className="w-[200px] text-right">Forbrugt / estimat</Th>
            <Th className="w-[150px]">Fakturering</Th>
            <Th className="w-[140px]">Status</Th>
          </tr>
        </thead>
        <tbody>
          {data.data.map((milestone) => {
            const usage = estimateUsage(milestone);

            return (
              <Tr key={milestone.id}>
                <Td>
                  <span className="font-medium">{milestone.name}</span>
                  {showBoard && milestone.board_name ? (
                    <span className="ml-2 text-xs text-ink-muted">{milestone.board_name}</span>
                  ) : null}
                </Td>
                <Td className="text-right">
                  <span className="tabular">
                    <HoursValue value={milestone.used_hours} />
                    <span className="text-ink-muted"> / </span>
                    {milestone.estimated_hours > 0 ? (
                      <HoursValue value={milestone.estimated_hours} className="text-ink-muted" />
                    ) : (
                      <span
                        className="text-ink-muted"
                        title={milestone.out_of_scope ? OUT_OF_SCOPE_HINT : undefined}
                      >
                        —
                      </span>
                    )}
                  </span>
                  {usage === null ? null : <Progress value={usage} className="mt-1.5" />}
                  {/* What it gave up when it closed - the drop on Burndown. */}
                  {milestone.unused_hours ? (
                    <div className="mt-1 text-xs text-ink-muted tabular">
                      <HoursValue value={milestone.unused_hours} /> ubrugt
                    </div>
                  ) : null}
                </Td>
                <Td>
                  <MilestoneReadiness milestone={milestone} />
                </Td>
                <Td>
                  <MilestoneState milestone={milestone} />
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>
      {caption ? (
        <CardBody>
          <p className="text-xs text-ink-muted">{caption}</p>
        </CardBody>
      ) : null}
    </Card>
  );
}

/**
 * Fanens emne, så de tre koblingsbeskeder kan skrives én gang og bruges af begge faner.
 *
 * Timer og milepæle går den **samme** kæde — kontraktnummer, projekt, tavle — og falder derfor
 * på de samme tre led. Kun ordet for det, der mangler, er forskelligt.
 */
interface LinkSubject {
  /** Bestemt flertal, stort begyndelsesbogstav: "Timerne", "Milepælene". Starter en sætning. */
  definite: string;
  /** Bestemt flertal, småt: "timerne", "faserne". Står inde i en sætning. */
  inline: string;
  /** Titlen når kæden er hel, men der ikke er noget at vise. */
  emptyTitle: string;
  /** Hvorfor der intet er, når det ikke giver sig selv af titlen. Udelades for timer og faser. */
  emptyDescription?: string;
}

/**
 * Teksten under 'Reel timepris'.
 *
 * Prisen alene siger ikke, hvorfor den er, som den er. Hintet gør: hvor mange af timerne der
 * ikke er fakturerbare, og hvor langt prisen ligger fra den aftalte. Tallene er serverens — her
 * sættes de kun sammen til en sætning.
 *
 * De to tomme tilfælde skal kunne skelnes: **ingen timer** er en aftale, der ikke er arbejdet
 * på endnu, mens **ingen timepris** er timer uden en sats i hverken projektet eller aftalen. Det
 * første går over af sig selv; det andet er en opgave til nogen.
 */
/** "1 ændring" / "3 ændringer" — dansk ental og flertal, så hintet kan læses som en sætning. */
function changeCount(count: number): string {
  return count === 1 ? '1 ændring' : `${count} ændringer`;
}

/**
 * 'CR Sum' under kontraktsummen: hvad de godkendte ændringer lægger oven i den aftalte sum.
 *
 * Kun `Uden for scope` er penge — se `changeRequestTotals` i @codepass/shared for hvorfor, og
 * for de to porte (skubbet til OnePro, ikke arkiveret) anmodningen skal igennem.
 *
 * **Tre tilstande, ikke to.** Er der ingen anmodninger overhovedet, står der ingenting: en
 * `CR Sum: 0 kr` på hver eneste aftale uden ændringer ville være støj. Er der anmodninger, men
 * ingen af dem fakturerbare, siges det med rene ord — nul er dér et svar, ikke en tom plads.
 *
 * En kontrakt læst fra databasen har ikke feltet og falder tilbage på tillægsaftale-hintet,
 * som kortet havde før.
 */
function ChangeRequestSumHint({ contract }: { contract: ContractDetail }) {
  const cr = contract.change_requests;

  if (!cr || cr.billable_count + cr.non_billable_count === 0) {
    if ((contract.counts['amendments'] ?? 0) > 0) {
      return (
        <>
          Inkl. tillæg: <Money value={contract.billed_incl_amendments_excl_vat} decimals={0} />{' '}
          faktureret
        </>
      );
    }
    return null;
  }

  if (cr.billable_count === 0) {
    return <>CR Sum: 0 kr · {changeCount(cr.non_billable_count)} uden beregning</>;
  }

  return (
    <>
      CR Sum: <Money value={cr.billable_amount_excl_vat} decimals={0} /> ·{' '}
      {changeCount(cr.billable_count)}
    </>
  );
}

/**
 * Teksten under 'Timer af loft'.
 *
 * Loftet indeholder timerne fra alle ændringsanmodninger, og hintet siger hvor stor en del af
 * det, de står for: `CR-timer / (CR-timer + oprindeligt loft)`. Uden den linje ville loftet
 * bare være et andet tal end det, der står i Dynamics, og ingen kunne se hvorfor.
 *
 * Under en halv procent skrives ikke: det ville stå som "0 %", og et kort, der gør opmærksom
 * på ingenting, er værre end et tomt.
 *
 * **Og tiden i "General", der ikke tæller** (15-10-2026). Uden den ville kortet og Timer-fanen,
 * der stadig viser opgaverne i General, sige to forskellige tal for det samme forbrug.
 */
function HoursCapHint({ contract }: { contract: ContractDetail }) {
  const general = contract.general_hours ?? 0;
  const generalText = general > 0 ? `${formatHours(general)} timer i General tæller ikke` : null;
  if (!contract.hours_cap) {
    return <>{['Ingen timeloft', generalText].filter(Boolean).join(' · ')}</>;
  }

  const share = contract.change_requests?.share_of_estimate_pct ?? null;
  const shareText =
    share === null || share < 0.5 ? null : `${formatPercent(share)} af loftet fra ændringer`;

  const parts = [shareText, generalText].filter(Boolean);
  return parts.length === 0 ? null : <>{parts.join(' · ')}</>;
}

function RealRateHint({ rate }: { rate: EffectiveHourlyRate | undefined }) {
  if (!rate) return null;
  if (rate.total_hours <= 0) return <>Ingen timer registreret</>;
  if (rate.real_rate_excl_vat === null) {
    return <>Ingen timepris på {formatHours(rate.total_hours)} registrerede timer</>;
  }

  const gap = rate.below_agreed_pct;

  return (
    <>
      {formatHours(rate.non_billable_hours)} af {formatHours(rate.total_hours)} timer ikke
      fakturerbare
      {gap === null ? null : (
        <>
          {' · '}
          {/* Under en halv procent er ikke en afvigelse, men afrunding. */}
          {Math.abs(gap) < 0.5
            ? 'på niveau med aftalt'
            : `${formatPercent(Math.abs(gap))} ${gap > 0 ? 'under' : 'over'} aftalt`}
        </>
      )}
    </>
  );
}

const HOURS_SUBJECT: LinkSubject = {
  definite: 'Timerne',
  inline: 'timerne',
  emptyTitle: 'Ingen timer registreret på kontrakten',
};

const MILESTONE_SUBJECT: LinkSubject = {
  definite: 'Milepælene',
  inline: 'faserne',
  emptyTitle: 'Ingen faser på aftalens tavle',
};

/**
 * Ændringsanmodningerne hænger på projektet og ikke på tavlen, så kæden er ét led kortere end
 * timernes: mangler kontraktnummeret eller projektet, er der ingen anmodninger at vise, mens en
 * manglende tavle er dem uvedkommende.
 */
const CHANGE_REQUEST_SUBJECT: LinkSubject = {
  definite: 'Ændringsanmodningerne',
  inline: 'ændringsanmodningerne',
  emptyTitle: 'Ingen åbne ændringsanmodninger sendt til OnePro',
  emptyDescription:
    'Fanen viser de anmodninger, der er skubbet til OnePro som arbejde og ikke er arkiveret ' +
    'igen — de samme, som tallene foroven er regnet af. Anmodninger under vurdering og ' +
    'arkiverede står i Orchestrator.',
};

/**
 * Hvorfor fanen er tom.
 *
 * En tom fane kan betyde to vidt forskellige ting: at der ikke er noget på aftalen, eller at
 * den aldrig er blevet koblet til et projekt i Orchestrator. Kun den ene er en opgave til
 * nogen, og beskeden skal sige hvilken - og hvor feltet saettes.
 *
 * Kaeden er: aftalens nummer i `contract_ids` paa projektet, og projektets `onepro_board_id`
 * paa tavlen i OnePro. Mangler ét af de to led, kommer hverken timer eller faser frem.
 */
function linkEmptyState(
  status: HoursLinkStatus | undefined,
  projects: string[] | undefined,
  subject: LinkSubject,
): { title: string; description?: string } {
  const project = projects?.length ? projects.join(', ') : null;

  switch (status) {
    case 'no_contract_no':
      return {
        title: 'Aftalen har intet kontraktnummer',
        description:
          `${subject.definite} kobles på kontraktnummeret fra Dynamics. Udfyld det på aftalen, ` +
          'og opret derefter forbindelsen til projektet i Orchestrator.',
      };
    case 'no_project':
      return {
        title: 'Kontrakten er ikke koblet til et projekt',
        description:
          'Ingen projekter i Orchestrator har denne kontrakt i feltet Kontrakter. Tilføj ' +
          `kontraktnummeret på projektet, så samles ${subject.inline} her.`,
      };
    case 'no_board':
      return {
        title: 'Projektet mangler en tavle i OnePro',
        description: project
          ? `Projektet "${project}" er koblet til kontrakten, men har ingen OnePro-tavle valgt. ` +
            `Vælg tavlen på projektet i Orchestrator, så vises ${subject.inline} her.`
          : 'Projektet er koblet til kontrakten, men har ingen OnePro-tavle valgt.',
      };
    default:
      return { title: subject.emptyTitle, description: subject.emptyDescription };
  }
}

/**
 * Handlingen paa en raekke paa fanen Timer.
 *
 * En opgave med status 'Invoiced' (vist som 'Faktureret') viser **Vis faktura** og foerer til fakturaen, den er paa -
 * det er det, man vil vide om den. De oevrige viser deres underopgaver, som foer.
 *
 * Kender Codepass ikke fakturaen - statussen er sat i OnePro, eller fakturaen er oprettet foer
 * sidesporet fandtes - staar knappen slaaet fra med en forklaring frem for at foere ingen steder.
 */
function HourAction({ hour, onShowTasks }: { hour: ContractHourItem; onShowTasks: () => void }) {
  const { user } = useAuth();
  if (hour.status === INVOICED_STATUS) {
    /* The task carries the invoice's number, not its status - see `canOpenInvoice`. */
    const blocked = !hour.invoice
      ? 'Codepass kender ikke fakturaen, opgaven er på'
      : !canOpenInvoice(user, null)
        ? 'Din rolle kan ikke åbne fakturaen'
        : null;
    if (!hour.invoice || blocked) {
      return (
        <Button size="sm" variant="secondary" disabled title={blocked ?? undefined}>
          Vis faktura
        </Button>
      );
    }

    return (
      <Link
        to={invoicePath({ id: hour.invoice.id, bc_invoice_id: hour.invoice.invoice_no })}
        title={hour.invoice.invoice_no ? `Faktura ${hour.invoice.invoice_no}` : undefined}
      >
        <Button size="sm" variant="secondary">
          Vis faktura
        </Button>
      </Link>
    );
  }

  return (
    <Button
      size="sm"
      variant="secondary"
      disabled={hour.task_count === 0}
      title={hour.task_count === 0 ? 'Ingen underopgaver på denne opgave' : undefined}
      onClick={onShowTasks}
    >
      Vis opgaver{hour.task_count > 0 ? ` (${hour.task_count})` : ''}
    </Button>
  );
}

/**
 * The tasks on the board the tab leaves out: service agreement work, which is the customer's
 * service agreement's and not the project's. See `countsAsContractHours`.
 */
function otherSourceText(other: { count: number; hours: number }): string {
  const one = other.count === 1;
  return (
    `Tavlen har ${other.count} ${one ? 'opgave' : 'opgaver'} på ${formatHours(other.hours)} ` +
    `timer, der er markeret Serviceaftale i OnePro og hører til kundens serviceaftale.`
  );
}

/**
 * The other side, on a service agreement: the tasks on its board that are not marked
 * 'Serviceaftale' are the original project's work. See `countsAsContractHours`.
 */
function projectWorkText(other: { count: number; hours: number }): string {
  const one = other.count === 1;
  return (
    `Tavlen har ${other.count} ${one ? 'opgave' : 'opgaver'} på ${formatHours(other.hours)} ` +
    `timer, der ikke er markeret Serviceaftale i OnePro. De hører til projektet på tavlen.`
  );
}

function HoursTab({ contractId }: { contractId: string }) {
  const [openHour, setOpenHour] = useState<ContractHourItem | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['contract', contractId, 'hours'],
    queryFn: () =>
      api.get<ContractHoursResponse>(`/contracts/${contractId}/hours`, {
        limit: 100,
        offset: 0,
      }),
  });

  if (isLoading) return <Skeleton className="h-48" />;
  const other = data?.other_source;
  if (!data || data.data.length === 0) {
    /*
     * A board with only service agreement work: say so, rather than that nothing is registered.
     */
    const empty =
      other && other.count > 0
        ? {
            title: 'Kun serviceaftale-opgaver på tavlen',
            description: `${otherSourceText(other)} De tæller ikke som aftalens timer.`,
          }
        : linkEmptyState(data?.link_status, data?.link_projects, HOURS_SUBJECT);
    return (
      <Card>
        <EmptyState title={empty.title} description={empty.description} />
      </Card>
    );
  }

  return (
    <Card>
      <Table>
        <thead>
          <tr>
            <Th className="w-[110px]">Dato</Th>
            <Th>Konsulent</Th>
            <Th>Opgave</Th>
            <Th className="w-[100px] text-right">Timer</Th>
            <Th className="w-[100px] text-right">Estimat</Th>
            <Th className="w-[150px]">Status</Th>
            <Th className="w-[140px]">Handlinger</Th>
          </tr>
        </thead>
        <tbody>
          {data.data.map((hour) => (
            <Tr key={hour.id}>
              {/* Samme kolonne og samme formatering som Dato paa /timer: todos.created_at. */}
              <Td className="tabular">{formatDate(hour.work_date)}</Td>
              <Td>{hour.consultant?.name ?? '—'}</Td>
              <Td>{hour.task?.title ?? <span className="text-danger">Ikke matchet</span>}</Td>
              <Td className="text-right">
                <HoursValue value={hour.duration_hours} />
              </Td>
              <Td className="text-right">
                {hour.estimate_hours === null ? (
                  <span className="text-ink-muted">—</span>
                ) : (
                  <HoursValue value={hour.estimate_hours} />
                )}
              </Td>
              <Td>
                {/* OnePro's egen status, saa kolonnen stemmer med /timer og med OnePro selv. */}
                <TimeEntryStatusBadge status={hour.status} />
              </Td>
              <Td>
                <HourAction hour={hour} onShowTasks={() => setOpenHour(hour)} />
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
      {data.total > data.data.length ? (
        <p className="px-3 py-2 text-xs text-ink-muted">
          Viser de {data.data.length} nyeste af {data.total}. Brug Timer-siden for fuld filtrering.
        </p>
      ) : null}
      {other && other.count > 0 ? (
        <p className="px-3 py-2 text-xs text-ink-muted">
          {otherSourceText(other)} De er ikke med her og tæller ikke i Timer af loft.
        </p>
      ) : null}

      {openHour ? (
        <SubtaskModal
          todoId={openHour.id}
          title={openHour.task?.title ?? null}
          subtitle={openHour.description ?? undefined}
          onClose={() => setOpenHour(null)}
        />
      ) : null}
    </Card>
  );
}

/**
 * Kortene på en serviceaftale.
 *
 * Fire og ikke fem, og ikke de samme fire:
 *
 *   Kontraktsum       timeprisen — det kunden betaler pr. time. En serviceaftale har ingen sum
 *                     at fakturere af, så det er det tal, aftalen lyder på. Samme valg som
 *                     kolonnen på /kontrakter.
 *   Faktureret        beløbet alene. Ingen procent: der er ingen sum at måle det mod.
 *   Timer pr. opgave  det, en opgave må bruge, før kunden skal godkende resten. Forbruget står
 *                     under.
 *   Reel timepris     som på en projektaftale, men regnet til kundens timepris — se
 *                     `serviceAgreementValue` i @codepass/shared.
 *
 * **Ingen buffer.** Bufferen lægges ikke på en serviceaftale — se `applyInvoiceBuffer` — så et
 * kort, der altid stod på 0 kr, ville kun sige noget, der ikke kan være anderledes.
 */
function ServiceAgreementCards({
  contract,
  terms,
}: {
  contract: ContractDetail;
  terms: ServiceAgreementTerms;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <MetricCard
        label="Kontraktsum"
        value={
          terms.hourly_rate_excl_vat === null ? (
            '—'
          ) : (
            <Money value={terms.hourly_rate_excl_vat} decimals={0} />
          )
        }
        hint={
          terms.hourly_rate_excl_vat === null
            ? "Ingen 'Løbende serviceaftale' på kunden i CRM"
            : 'pr. time'
        }
        tone={terms.hourly_rate_excl_vat === null ? 'warning' : 'neutral'}
      />
      <MetricCard
        label="Faktureret"
        value={<Money value={contract.billed_excl_vat} decimals={0} />}
      />
      <MetricCard
        label="Timer pr. opgave"
        value={terms.hours_per_task === null ? '—' : formatHours(terms.hours_per_task)}
        hint={
          terms.hours_per_task === null
            ? 'Ikke sat i CRM'
            : `${formatHours(contract.used_hours)} timer brugt i alt`
        }
        tone={terms.hours_per_task === null ? 'warning' : 'neutral'}
      />
      <MetricCard
        label="Reel timepris"
        value={
          contract.effective_rate?.real_rate_excl_vat === null ||
          contract.effective_rate === undefined ? (
            '—'
          ) : (
            <Money value={contract.effective_rate.real_rate_excl_vat} decimals={0} />
          )
        }
        hint={<RealRateHint rate={contract.effective_rate} />}
        tone={contract.effective_rate?.level === 'warning' ? 'warning' : 'neutral'}
      />
    </div>
  );
}

/** Vilkårene i Oversigt-fanens beskrivelse. Samme plads som abonnementets beløb på et abonnement. */
function ServiceTermsBox({ terms }: { terms: ServiceAgreementTerms }) {
  return (
    <div className="rounded-md bg-canvas p-3 text-sm">
      <p>
        Serviceaftale:{' '}
        {terms.hourly_rate_excl_vat === null ? (
          '—'
        ) : (
          <Money value={terms.hourly_rate_excl_vat} decimals={0} />
        )}{' '}
        pr. time, {terms.hours_per_task === null ? '—' : formatHours(terms.hours_per_task)} timer
        pr. opgave
      </p>
      <p className="mt-1 text-xs text-ink-muted">
        Alle timer faktureres til timeprisen fra &laquo;Løbende serviceaftale&raquo; på kunden i
        CRM. En opgave, der bruger flere timer end timer pr. opgave, skal godkendes af kunden.
      </p>
    </div>
  );
}

/** Månedens navn, som oversigten og filteret viser det. */
function monthLabel(month: string): string {
  return month === UNCOMPLETED_MONTH ? 'Ikke færdigmeldt' : formatMonthYear(`${month}-01`);
}

/**
 * Timer-fanen på en serviceaftale: opgaverne pr. måned, de blev meldt færdige i.
 *
 * Tre ting på én fane:
 *
 *   oversigten  alle måneder som søjler, samme diagram som 'Fakturering pr. måned' på
 *               dashboardet. Et klik på en søjle filtrerer tabellen til måneden.
 *   filteret    den samme måned som en liste — også 'Ikke færdigmeldt', som ingen søjle har.
 *   tabellen    opgaverne, med en overskrift pr. måned, de brugte timer ved siden af dem, der
 *               faktureres (`invoice_hours`, valgt i OnePro), og de timer, der ligger ud over
 *               timer pr. opgave. Det er dem, kunden skal godkende.
 *
 * Alle tal er serverens — se `groupServiceHoursByMonth`. Fanen lægger kun sammen, hvad den viser.
 */
function ServiceHoursTab({ contractId }: { contractId: string }) {
  /** Den valgte måned, `YYYY-MM` eller `UNCOMPLETED_MONTH`. Tom betyder alle. */
  const [month, setMonth] = useState('');
  const [openHour, setOpenHour] = useState<ServiceHourItem | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['contract', contractId, 'hours-by-month'],
    queryFn: () => api.get<ContractHoursByMonthResponse>(`/contracts/${contractId}/hours-by-month`),
  });

  const timeline = useMemo(() => serviceHoursTimeline(data?.months ?? [], new Date()), [data]);

  if (isLoading) return <Skeleton className="h-48" />;
  const other = data?.other_source;
  if (!data || data.months.length === 0) {
    /* A board with only the project's work: say so, rather than that nothing is registered. */
    const empty =
      other && other.count > 0
        ? { title: 'Ingen serviceaftale-opgaver på tavlen', description: projectWorkText(other) }
        : linkEmptyState(data?.link_status, data?.link_projects, HOURS_SUBJECT);
    return (
      <Card>
        <EmptyState title={empty.title} description={empty.description} />
      </Card>
    );
  }

  const visible = month ? data.months.filter((row) => row.month === month) : data.months;
  const toggleMonth = (key: string): void => setMonth((current) => (current === key ? '' : key));

  /*
   * Opsummeringen følger filteret, så tallene over tabellen er tabellens egne. Ud over-summen er
   * null, når grænsen ikke kendes — da står der em-streg frem for et nul, der lignede et svar.
   */
  const shownHours = visible.reduce((total, row) => total + row.hours, 0);
  const shownInvoiceHours = visible.reduce((total, row) => total + row.invoice_hours, 0);
  const shownOverLimit =
    data.hours_per_task === null
      ? null
      : visible.reduce((total, row) => total + (row.over_limit_hours ?? 0), 0);
  const shownTasks = visible.reduce((total, row) => total + row.task_count, 0);
  const overCap = visible.reduce(
    (total, row) => total + row.rows.filter((hour) => (hour.over_limit_hours ?? 0) > 0).length,
    0,
  );

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex items-center justify-between gap-3">
            <CardTitle>Timer pr. måned</CardTitle>
            {month ? (
              <Button size="sm" variant="ghost" onClick={() => setMonth('')}>
                Vis alle måneder
              </Button>
            ) : null}
          </CardHeader>
          <CardBody>
            {timeline.length === 0 ? (
              <p className="text-xs text-ink-muted">Ingen opgaver er meldt færdige endnu.</p>
            ) : (
              <BarChart
                items={timeline.map((row) => ({
                  key: row.month,
                  label: monthLabel(row.month),
                  value: row.hours,
                }))}
                format={(value) => `${formatHours(value)} t`}
                selected={month || null}
                onSelect={toggleMonth}
              />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{month ? monthLabel(month) : 'Alle måneder'}</CardTitle>
          </CardHeader>
          <CardBody>
            <dl className="space-y-2 text-sm">
              <Row label="Opgaver" value={String(shownTasks)} />
              <Row label="Timer brugt" value={<HoursValue value={shownHours} />} />
              <Row label="Timer til fakturering" value={<HoursValue value={shownInvoiceHours} />} />
              <Row
                label="Timer pr. opgave"
                value={
                  data.hours_per_task === null ? '—' : <HoursValue value={data.hours_per_task} />
                }
              />
              <Row
                label="Timer ud over"
                value={shownOverLimit === null ? '—' : <HoursValue value={shownOverLimit} />}
              />
              <Row label="Opgaver over timer pr. opgave" value={String(overCap)} />
            </dl>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>Opgaver pr. måned</CardTitle>
          <select
            value={month}
            onChange={(event) => setMonth(event.target.value)}
            aria-label="Vis måned"
            className="h-8 rounded-md border border-line bg-surface px-2 text-sm"
          >
            <option value="">Alle måneder</option>
            {data.months.map((row) => (
              <option key={row.month} value={row.month}>
                {monthLabel(row.month)}
              </option>
            ))}
          </select>
        </CardHeader>

        {visible.length === 0 ? (
          <EmptyState title={`Ingen opgaver meldt færdige i ${monthLabel(month)}`} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th className="w-[110px]">Færdig</Th>
                <Th>Konsulent</Th>
                <Th>Opgave</Th>
                <Th className="w-[100px] text-right">Brugt</Th>
                <Th
                  className="w-[100px] text-right"
                  title="Timerne udvikleren har valgt at fakturere i OnePro. De brugte timer, når der ikke er valgt et tal."
                >
                  Faktureres
                </Th>
                <Th
                  className="w-[100px] text-right"
                  title="Timerne ud over timer pr. opgave. Det er dem, kunden skal godkende."
                >
                  Ud over
                </Th>
                <Th className="w-[150px]">Status</Th>
                <Th className="w-[140px]">Handlinger</Th>
              </tr>
            </thead>
            <tbody>
              {visible.map((group) => (
                <Fragment key={group.month}>
                  <Tr>
                    <Td colSpan={8} className="bg-canvas">
                      <span className="text-sm font-semibold">{monthLabel(group.month)}</span>
                      <span className="ml-2 text-xs text-ink-muted">
                        {group.task_count} {group.task_count === 1 ? 'opgave' : 'opgaver'} ·{' '}
                        {formatHours(group.hours)} timer brugt · {formatHours(group.invoice_hours)}{' '}
                        faktureres
                        {group.over_limit_hours === null
                          ? null
                          : ` · ${formatHours(group.over_limit_hours)} ud over`}
                      </span>
                    </Td>
                  </Tr>
                  {group.rows.map((hour) => (
                    <Tr key={hour.id}>
                      <Td className="tabular">{formatDate(hour.completed_at)}</Td>
                      <Td>{hour.consultant?.name ?? '—'}</Td>
                      <Td>
                        {hour.task?.title ?? <span className="text-danger">Ikke matchet</span>}
                      </Td>
                      <Td className="text-right">
                        <HoursValue value={hour.duration_hours} />
                      </Td>
                      <Td className="text-right">
                        <HoursValue value={hour.invoice_hours} />
                      </Td>
                      <Td className="text-right">
                        {hour.over_limit_hours === null ? (
                          <span className="text-ink-muted">—</span>
                        ) : hour.over_limit_hours > 0 ? (
                          <HoursValue value={hour.over_limit_hours} className="text-amber-700" />
                        ) : (
                          <span className="text-ink-muted">0,00</span>
                        )}
                      </Td>
                      <Td>
                        <TimeEntryStatusBadge status={hour.status} />
                      </Td>
                      <Td>
                        <HourAction hour={hour} onShowTasks={() => setOpenHour(hour)} />
                      </Td>
                    </Tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </Table>
        )}
        {other && other.count > 0 ? (
          <p className="px-3 py-2 text-xs text-ink-muted">
            {projectWorkText(other)} De er ikke med her.
          </p>
        ) : null}

        {openHour ? (
          <SubtaskModal
            todoId={openHour.id}
            title={openHour.task?.title ?? null}
            subtitle={openHour.description ?? undefined}
            onClose={() => setOpenHour(null)}
          />
        ) : null}
      </Card>
    </div>
  );
}

/**
 * Page size on the tab Fakturaer. Ten, as the invoice history on /kunder/:id: the tab sits under
 * the cards, and a long table would push the page's end out of sight.
 */
const INVOICE_LIMIT = 10;

/** The statuses a Business Central invoice has here - see `mapInvoice` in the API. */
const INVOICE_TAB_STATUSES = ['draft', 'posted', 'cancelled'] as const;

/** The tab's filters. Empty is all, as on /fakturering. */
interface InvoiceFilters {
  status: string;
  from: string;
  to: string;
  /** `only` the invoices shared with other contracts, `none` those for this one alone. */
  shared: string;
}

const NO_INVOICE_FILTERS: InvoiceFilters = { status: '', from: '', to: '', shared: '' };

function InvoicesTab({ contractId }: { contractId: string }) {
  /* A draft is 'Se fakturaudkast', a sent invoice 'Se fakturahistorik' - see `canOpenInvoice`. */
  const { user } = useAuth();
  const [filters, setFilters] = useState<InvoiceFilters>(NO_INVOICE_FILTERS);
  const [offset, setOffset] = useState(0);

  /* Another contract in the same component - its filters and page do not carry over. */
  useEffect(() => {
    setFilters(NO_INVOICE_FILTERS);
    setOffset(0);
  }, [contractId]);

  const update = (changes: Partial<InvoiceFilters>): void => {
    setFilters((current) => ({ ...current, ...changes }));
    // Et aendret filter goer den aktuelle side meningsloes - start forfra.
    setOffset(0);
  };
  const filtered = Object.values(filters).some(Boolean);

  const { data, isLoading } = useQuery({
    queryKey: ['contract', contractId, 'invoices', filters, offset],
    queryFn: () =>
      api.get<Paginated<InvoiceListItem>>(`/contracts/${contractId}/invoices`, {
        status: filters.status || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
        shared: filters.shared || undefined,
        limit: INVOICE_LIMIT,
        offset,
      }),
    /** Den forrige side bliver staaende mens den naeste hentes - ingen blinkende tabel. */
    placeholderData: (previous) => previous,
  });

  if (isLoading) return <Skeleton className="h-48" />;
  /* No invoices at all - no filter bar to offer. */
  if (!filtered && (!data || data.data.length === 0)) {
    return (
      <Card>
        <EmptyState title="Ingen fakturaer på kontrakten endnu" />
      </Card>
    );
  }

  /* An older server answers with `data` alone - every invoice on one page. */
  const total = data?.total ?? data?.data.length ?? 0;

  return (
    <Card>
      <div className="flex flex-wrap items-end gap-3 border-b border-line p-3">
        <div className="w-full sm:w-40">
          <span className="field-label">Status</span>
          <Select
            value={filters.status}
            onChange={(event) => update({ status: event.target.value })}
          >
            <option value="">Alle statusser</option>
            {INVOICE_TAB_STATUSES.map((status) => (
              <option key={status} value={status}>
                {INVOICE_STATUS_LABELS[status]}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-full sm:w-40">
          <span className="field-label">Fakturadato fra</span>
          <Input
            type="date"
            value={filters.from}
            max={filters.to || undefined}
            onChange={(event) => update({ from: event.target.value })}
          />
        </div>
        <div className="w-full sm:w-40">
          <span className="field-label">Fakturadato til</span>
          <Input
            type="date"
            value={filters.to}
            min={filters.from || undefined}
            onChange={(event) => update({ to: event.target.value })}
          />
        </div>
        <div className="w-full sm:w-48">
          <span className="field-label">Aftaler</span>
          <Select
            value={filters.shared}
            onChange={(event) => update({ shared: event.target.value })}
          >
            <option value="">Alle fakturaer</option>
            <option value="none">Kun denne aftale</option>
            <option value="only">Delt med andre aftaler</option>
          </Select>
        </div>
        {filtered ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setFilters(NO_INVOICE_FILTERS);
              setOffset(0);
            }}
          >
            Nulstil filtre
          </Button>
        ) : null}
        <span className="ml-auto self-center text-xs text-ink-muted">
          {total === 1 ? '1 faktura' : `${total} fakturaer`}
        </span>
      </div>

      {!data || data.data.length === 0 ? (
        <EmptyState
          title="Ingen fakturaer matcher filtrene"
          description="Ret filtrene, eller nulstil dem for at se alle aftalens fakturaer."
        />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Faktura</Th>
                <Th className="w-[110px]">Grundlag</Th>
                <Th className="w-[150px]">Forfaldsdato</Th>
                <Th className="text-right">Ekskl. moms</Th>
                <Th className="text-right">Inkl. moms</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((invoice) => {
                /*
                 * An invoice shared with other contracts - listed on each. Its own part is what the
                 * card Faktureret counts, so it stands under the invoice's total.
                 */
                const others = (invoice.contracts ?? []).filter(
                  (other) =>
                    other.id !== contractId &&
                    other.contract_no?.toLowerCase() !== contractId.toLowerCase(),
                );
                const ownPart = others.length > 0 ? invoice.contract_amount_excl_vat : undefined;

                return (
                  <Tr key={invoice.id}>
                    <Td>
                      <PermittedLink
                        to={invoicePath(invoice)}
                        allowed={canOpenInvoice(user, invoice.status)}
                        className="font-medium hover:underline"
                      >
                        {invoice.bc_invoice_id ?? invoice.invoice_no_proposal ?? 'Kladde'}
                      </PermittedLink>
                      <span className="block text-xs text-ink-muted">
                        Oprettet {formatDate(invoice.created_at)}
                        {invoice.generated_by === 'agent' ? ' · automatisk' : ''}
                      </span>
                      {others.length > 0 ? (
                        <span className="block text-xs text-ink-muted">
                          Delt med{' '}
                          {others.map((other) => other.contract_no ?? other.title).join(', ')}
                        </span>
                      ) : null}
                    </Td>
                    {/* Dansk label, ikke den raa noegle: 'hours' er API'ets sprog, ikke brugerens. */}
                    <Td className="text-xs">
                      {invoice.invoice_basis ? INVOICE_BASIS_LABELS[invoice.invoice_basis] : '—'}
                    </Td>
                    <Td className="text-xs tabular">{formatDate(invoice.due_date)}</Td>
                    <Td className="text-right">
                      <Money value={invoice.amount_excl_vat} />
                      {ownPart === undefined ? null : (
                        <span className="block text-xs text-ink-muted">
                          {ownPart === null ? (
                            'Aftalens andel ukendt'
                          ) : (
                            <>
                              Heraf aftalen: <Money value={ownPart} />
                            </>
                          )}
                        </span>
                      )}
                    </Td>
                    <Td className="text-right">
                      <Money value={invoice.amount_incl_vat} />
                    </Td>
                    <Td>
                      <InvoiceStatusBadge status={invoice.status} payment={invoice} />
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
          <Pagination total={total} limit={INVOICE_LIMIT} offset={offset} onChange={setOffset} />
        </>
      )}
    </Card>
  );
}

/**
 * Kontraktlinjerne fra CRM: arbejdets omfang, linje for linje. PRD par. 5.2.
 *
 * **Kun beskrivelse og timer** (15-10-2026). Estimatlinjerne står til 0 kr i CRM - pengene ligger
 * på `Udviklingstimer`-linjen, som API'et ikke sender med - så stykpris, i alt og enhed var en
 * kolonne af nuller. Beløbet står på kortet 'Kontraktsum'. En linje, der ikke er timer -
 * `Applikations drift`, fx - har ingen timer at vise.
 */
function LinesTab({ contractId }: { contractId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['contract', contractId, 'lines'],
    queryFn: () => api.get<{ data: ContractLineItem[] }>(`/contracts/${contractId}/lines`),
  });

  if (isLoading) return <Skeleton className="h-48" />;
  if (!data || data.data.length === 0) {
    return (
      <Card>
        <EmptyState
          title="Ingen kontraktlinjer"
          description="Kontraktlinjer følger med kontrakten fra kilden. Er der ingen, er der ikke lagt produktlinjer på aftalen."
        />
      </Card>
    );
  }

  return (
    <Card>
      <Table>
        <thead>
          <tr>
            {/* Ingen Nr.-kolonne: linjerne er selv nummererede i beskrivelsen ("01 - ..."). */}
            <Th>Beskrivelse</Th>
            <Th className="w-[120px] text-right">Antal timer</Th>
          </tr>
        </thead>
        <tbody>
          {data.data.map((line) => (
            <Tr key={line.id}>
              <Td>
                <span className="font-medium">{line.description}</span>
                {/*
                  Bogføringskontoen hører til en GetAccept-linje, produktnavnet til en
                  Dynamics-linje — aldrig begge på samme række. Produktet er værd at vise,
                  fordi det skiller estimatlinjerne fra den linje, der bærer pengene.
                */}
                {line.bc_account ? (
                  <span className="block text-xs text-ink-muted">Konto {line.bc_account}</span>
                ) : line.product_name ? (
                  <span className="block text-xs text-ink-muted">{line.product_name}</span>
                ) : null}
              </Td>
              <Td className="text-right tabular">
                {line.unit === 'hours' ? (
                  formatHours(line.quantity)
                ) : (
                  <span className="text-ink-muted">—</span>
                )}
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
}

/**
 * Hovedaftale-fanen: den reciprokke af Tillægsaftaler. Vises kun når kontrakten selv er en
 * tillægsaftale, og fører op ad kæden til moderaftalen. Samme tabelform som Tillægsaftaler,
 * så de to retninger ligner hinanden.
 */
function ParentTab({ parent }: { parent: NonNullable<ContractDetail['parent']> }) {
  return (
    <Card>
      <Table>
        <thead>
          <tr>
            <Th>Hovedaftale</Th>
            <Th className="text-right">Sum</Th>
            <Th>Signeret</Th>
            <Th>Status</Th>
          </tr>
        </thead>
        <tbody>
          <Tr>
            <Td>
              <Link to={`/kontrakter/${parent.id}`} className="font-medium hover:underline">
                {parent.title}
              </Link>
            </Td>
            <Td className="text-right">
              <Money value={parent.contract_sum_excl_vat} currency={parent.currency} decimals={0} />
            </Td>
            <Td className="text-xs tabular">{formatDate(parent.signed_date)}</Td>
            <Td>
              <ContractStatusBadge status={parent.status} />
            </Td>
          </Tr>
        </tbody>
      </Table>
    </Card>
  );
}

/**
 * Ændringsanmodningerne på aftalen — `orchestrator.change_requests`, se
 * contract-change-requests.ts i API'et.
 *
 * **Kun åbne anmodninger, der er sendt til OnePro.** Serveren afgrænser på begge dele: en
 * anmodning uden `pushed_to_onepro_at` er endnu ikke besluttet og kan stadig blive henlagt
 * eller afvist, og en arkiveret er trukket tilbage igen. Tilbage står det, der faktisk er
 * arbejde på aftalen lige nu.
 *
 * Det er **de samme to porte**, som kortene foroven tæller efter — se `changeRequestTotals` i
 * @codepass/shared. Tabellen og tallene skal kunne lægges sammen; vurderingen og historikken
 * hører i Orchestrator, ikke på en kontraktside.
 *
 * Beskrivelsen står **ikke** i tabellen. Den er et afsnit og ikke en celle, og et forsøg på at
 * klemme den ind ville enten klippe den midt i en sætning eller gøre hver række tre linjer høj.
 * Knappen 'Beskrivelse' åbner den i stedet, sammen med det øvrige, der er for langt til en
 * kolonne: scope-reference, anbefaling og noter.
 */
function ChangeRequestsCard({ contractId }: { contractId: string }) {
  const [open, setOpen] = useState<ChangeRequestItem | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['contract', contractId, 'change-requests'],
    queryFn: () => api.get<ChangeRequestsResponse>(`/contracts/${contractId}/change-requests`),
  });

  if (isLoading) return <Skeleton className="h-32" />;

  const requests = data?.data ?? [];

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Åbne ændringsanmodninger sendt til OnePro</CardTitle>
        </CardHeader>

        {requests.length === 0 ? (
          <EmptyState
            {...linkEmptyState(data?.link_status, data?.link_projects, CHANGE_REQUEST_SUBJECT)}
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th className="w-12 text-right">Nr.</Th>
                <Th>Anmodning</Th>
                <Th>Klassifikation</Th>
                <Th>Status</Th>
                <Th className="w-24 text-right">Timer</Th>
                <Th className="w-32 text-right">Beløb</Th>
                <Th className="w-28">Modtaget</Th>
                <Th className="w-32" />
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <Tr key={request.id}>
                  <Td className="text-right tabular text-ink-muted">{request.cr_number}</Td>
                  <Td>
                    <span className="font-medium">{request.title}</span>
                    {/*
                      Intet 'Arkiveret'-mærkat mere. Serveren leverer kun åbne anmodninger, så
                      mærkatet kunne aldrig vises — se contract-change-requests.ts.
                    */}
                    <span className="mt-0.5 block text-xs text-ink-muted">
                      {crLabel(CR_PRIORITY_LABELS, request.priority)} ·{' '}
                      {crLabel(CR_CHANNEL_LABELS, request.channel)}
                    </span>
                  </Td>
                  <Td className="text-xs">
                    {crLabel(CR_CLASSIFICATION_LABELS, request.classification)}
                  </Td>
                  <Td className="text-xs">{crLabel(CR_STATUS_LABELS, request.status)}</Td>
                  <Td className="text-right">
                    <HoursValue value={request.impact_hours} />
                  </Td>
                  <Td className="text-right">
                    {/* Em-streg og ikke 0 kr: beløbet er ikke sat på anmodningen. */}
                    {request.impact_dkk === null ? (
                      <span className="text-ink-muted">—</span>
                    ) : (
                      <Money value={request.impact_dkk} decimals={0} />
                    )}
                  </Td>
                  <Td className="text-xs tabular">{formatDate(request.received_at)}</Td>
                  <Td className="text-right">
                    <Button size="sm" variant="secondary" onClick={() => setOpen(request)}>
                      Beskrivelse
                    </Button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {open ? <ChangeRequestModal request={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}

/**
 * Anmodningens tekst i fuld længde.
 *
 * Alt det, der ikke kan stå i en tabelcelle: beskrivelsen, hvad den refererer til i aftalen,
 * hvad der anbefales, og noterne. Felter uden indhold udelades frem for at stå som tomme
 * overskrifter — de findes som tomme strenge i Orchestrator, ikke som noget nogen har skrevet.
 *
 * Ingen 'Arkiveret'-sektion: serveren leverer kun åbne anmodninger, så `archive_reason` ville
 * altid være tom. Se contract-change-requests.ts.
 */
function ChangeRequestModal({
  request,
  onClose,
}: {
  request: ChangeRequestItem;
  onClose: () => void;
}) {
  const sections: Array<{ label: string; value: string }> = [
    { label: 'Beskrivelse', value: request.description },
    { label: 'Reference i aftalen', value: request.scope_reference },
    { label: 'Anbefaling', value: crLabel(CR_RECOMMENDATION_LABELS, request.recommendation) },
    { label: 'Noter', value: request.notes },
    { label: 'Fakturareference', value: request.invoice_ref },
  ].filter((section) => section.value.trim() !== '' && section.value !== '—');

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`${request.cr_number}. ${request.title}`}
      description={`${crLabel(CR_CLASSIFICATION_LABELS, request.classification)} · ${crLabel(
        CR_STATUS_LABELS,
        request.status,
      )} · modtaget ${formatDate(request.received_at)}`}
    >
      <div className="space-y-4">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
          <Fact label="Timer" value={<HoursValue value={request.impact_hours} />} />
          <Fact
            label="Beløb"
            value={
              request.impact_dkk === null ? '—' : <Money value={request.impact_dkk} decimals={0} />
            }
          />
          <Fact label="Dage" value={String(request.impact_days)} />
          <Fact label="Frist" value={formatDate(request.due_at)} />
        </dl>

        {sections.map((section) => (
          <div key={section.label}>
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
              {section.label}
            </p>
            {/* `whitespace-pre-line`: teksten er skrevet med linjeskift, og de bærer mening. */}
            <p className="mt-1 whitespace-pre-line text-sm">{section.value}</p>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="mt-0.5">{value}</dd>
    </div>
  );
}

/**
 * The Burndown tab - the burndown report from /rapporter/burndown for the contract's board in
 * OnePro, `?contract=` instead of `?project=`. The same report, so the tab and the report cannot
 * say different things: the key figures, the curve and the caveats. Milestones and open tasks are
 * left to the Milepæle and Timer tabs. A contract with two boards gets a picker between them.
 */
function BurndownTab({ contractNo }: { contractNo: string }) {
  const [board, setBoard] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['report', 'burndown', 'contract', contractNo, board],
    queryFn: () =>
      api.get<ReportDocument>('/reports/burndown', {
        contract: contractNo,
        project: board ?? undefined,
      }),
    /* The board before stays while the next loads - never another contract's. */
    placeholderData: (previous, query) =>
      query?.queryKey[3] === contractNo ? previous : undefined,
  });

  if (isLoading) return <Skeleton className="h-64" />;
  if (error || !data) return <Alert tone="error">Kunne ikke hente burndown.</Alert>;

  const options = data.choice?.options ?? [];

  return (
    <div className="space-y-4">
      {options.length > 1 ? (
        <Select
          className="w-64 max-w-full"
          value={board ?? data.choice?.selected ?? ''}
          aria-label="Tavle"
          onChange={(event) => setBoard(event.target.value)}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      ) : null}

      {data.kpis.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {data.kpis.map((kpi) => (
            <MetricCard
              key={kpi.label}
              label={kpi.label}
              value={formatReportValue(kpi.type, kpi.value, data.currency)}
              hint={kpi.hint}
              tone={kpi.tone}
            />
          ))}
        </div>
      ) : null}

      <BurndownChart report={data} />

      {data.notes.length > 0 ? (
        <Alert tone="info" title="Forbehold">
          <ul className="list-disc space-y-1 pl-4">
            {data.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </Alert>
      ) : null}
    </div>
  );
}

/**
 * The Partneraftale tab - only there when the sale is referred. See
 * docs/partner/1-12-Partneraftale-Designnotat.md.
 *
 * Left, the agreement and the rates as they stand on the opportunity: a snapshot, so a new
 * agreement with the partner never changes this sale. Right and below, the commission on the
 * contract's posted invoices, line by line. The server works it out - see
 * `partnerCommission` in @codepass/shared - and the tab only lays it out.
 *
 * **Nothing is settled here.** Paid lines are "ready"; an admin approves them per partner on
 * /fakturering/provision, and from then on they show the statement they were paid out on.
 *
 * The agreement comes with the contract, so the card shows at once; the commission is the tab's
 * own call, because it reads every invoice on the contract in BC.
 */
function PartnerTab({
  contractId,
  referral,
  currency,
}: {
  contractId: string;
  referral: PartnerReferral;
  currency: string;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['contract', contractId, 'partner-commission'],
    queryFn: () =>
      api.get<PartnerCommissionResponse>(`/contracts/${contractId}/partner-commission`),
  });

  const agreement = referral.agreement;
  const lines = data?.lines ?? [];

  return (
    <div className="space-y-4">
      {data && data.problems.length > 0 ? (
        <Alert tone="error" title="Provisionen kan ikke opgøres fuldt ud">
          <ul className="list-disc space-y-1 pl-5">
            {data.problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </Alert>
      ) : null}
      {data?.notes.map((note) => (
        <Alert key={note} tone="warning">
          {note}
        </Alert>
      ))}

      <div className="grid gap-4 lg:grid-cols-5">
        <Card className="lg:col-span-2">
          <CardHeader className="flex items-center justify-between gap-3">
            <CardTitle>Partneraftale</CardTitle>
            {agreement?.agreement_no ? (
              <span className="tabular text-xs text-ink-muted">{agreement.agreement_no}</span>
            ) : null}
          </CardHeader>
          <CardBody>
            <dl className="space-y-2 text-sm">
              <Row label="Partner" value={agreement?.partner?.name ?? '—'} />
              <Row
                label="Kontaktperson"
                value={
                  agreement?.contact
                    ? [agreement.contact.name, agreement.contact.email].filter(Boolean).join(' · ')
                    : '—'
                }
              />
              <Row label="Status" value={<AgreementStatusBadge agreement={agreement} />} />
              <Row label="Provision udviklingstimer" value={rateLabel(referral.hours_rate_pct)} />
              <Row
                label="Provision løbende ydelser"
                value={rateLabel(referral.recurring_rate_pct)}
              />
              <Row label="Ikrafttrædelse" value={formatDate(agreement?.effective_date)} />
              {/* Only on an agreement that has been given notice - empty on every other. */}
              {agreement?.notice_received_date ? (
                <Row
                  label="Opsigelse modtaget"
                  value={formatDate(agreement.notice_received_date)}
                />
              ) : null}
              {agreement?.end_date ? (
                <Row label="Ophør pr." value={formatDate(agreement.end_date)} />
              ) : null}
              {agreement?.cvr_checked_date ? (
                <Row label="CVR aktivt pr." value={formatDate(agreement.cvr_checked_date)} />
              ) : null}
              {agreement?.signed_agreement_url ? (
                <Row
                  label="Signeret aftale"
                  value={
                    <a
                      href={agreement.signed_agreement_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 underline"
                    >
                      Åbn
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  }
                />
              ) : null}
            </dl>
          </CardBody>
        </Card>

        <div className="grid content-start gap-4 sm:grid-cols-2 lg:col-span-3">
          {isLoading ? (
            <Skeleton className="h-24 sm:col-span-2" />
          ) : (
            <>
              {/* In the order the money moves in BC: invoiced, paid, paid out. */}
              <MetricCard
                label="Afventer betaling"
                value={<Money value={data?.summary.awaiting_excl_vat ?? 0} currency={currency} />}
                hint="Provision på fakturaer, der endnu ikke er betalt"
              />
              <MetricCard
                label="Klar til afregning"
                value={<Money value={data?.summary.ready_excl_vat ?? 0} currency={currency} />}
                hint="Provision på linjer, kunden har betalt"
              />
              {/* Only once something has been paid out - before that it would always say 0 kr. */}
              {data && data.summary.settled_excl_vat !== 0 ? (
                <MetricCard
                  label="Afregnet"
                  value={<Money value={data.summary.settled_excl_vat} currency={currency} />}
                  hint="Udbetalt til partneren på tidligere opgørelser"
                />
              ) : null}
            </>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Provision på aftalens fakturaer</CardTitle>
          <p className="mt-1 text-xs text-ink-muted">
            Betalte linjer afregnes samlet pr. partner under Fakturering → Provision.
          </p>
        </CardHeader>

        {isLoading ? (
          <Skeleton className="m-4 h-32" />
        ) : error ? (
          <div className="p-4">
            <Alert tone="error">Kunne ikke hente provisionen på aftalen.</Alert>
          </div>
        ) : lines.length === 0 ? (
          <EmptyState
            title="Ingen bogførte fakturaer på aftalen endnu"
            description="Provisionen regnes af de fakturaer, der er bogført på aftalen i Business Central."
          />
        ) : (
          <CommissionLinesTable lines={lines} currency={currency} />
        )}
      </Card>
    </div>
  );
}
